class GallerySection {
  constructor(container) {
    this.container = container;
    this.galleryItems = container.querySelectorAll("[data-gallery-item]");
    this.modal = container.querySelector("[data-gallery-modal]");
    this.modalContent = container.querySelector("[data-modal-content]");
    this.closeButton = container.querySelector("[data-modal-close]");
    this.prevButton = container.querySelector("[data-modal-prev]");
    this.nextButton = container.querySelector("[data-modal-next]");
    this.backdrop = container.querySelector("[data-modal-backdrop]");

    this.currentIndex = 0;
    this.isModalOpen = false;
    this.hoverTimeouts = new Map();

    this.init();
  }

  init() {
    this.bindEvents();
    this.setupVideoElements();
  }

  bindEvents() {
    this.galleryItems.forEach((item, index) => {
      item.addEventListener("click", () => this.openModal(index));
      item.addEventListener("mouseenter", () =>
        this.handleItemHover(item, true)
      );
      item.addEventListener("mouseleave", () =>
        this.handleItemHover(item, false)
      );
    });

    this.closeButton?.addEventListener("click", () => this.closeModal());
    this.prevButton?.addEventListener("click", () => this.navigateModal(-1));
    this.nextButton?.addEventListener("click", () => this.navigateModal(1));
    this.backdrop?.addEventListener("click", () => this.closeModal());

    document.addEventListener("keydown", (e) => this.handleKeyboard(e));
  }

  setupVideoElements() {
    this.galleryItems.forEach((item) => {
      const video = item.querySelector("[data-gallery-video]");
      if (video) {
        video.addEventListener("loadedmetadata", () => {
          video.currentTime = 0;
        });
      }
    });
  }

  extractVideoId(url) {
    if (url.includes("youtube.com/watch?v=")) {
      return url.split("watch?v=")[1].split("&")[0];
    } else if (url.includes("youtu.be/")) {
      return url.split("youtu.be/")[1].split("?")[0];
    } else if (url.includes("youtube.com/embed/")) {
      return url.split("embed/")[1].split("?")[0];
    } else if (url.includes("vimeo.com/")) {
      return url.split("vimeo.com/")[1].split("?")[0];
    }
    return "";
  }

  getEmbedUrl(videoId, platform) {
    switch (platform) {
      case "youtube":
        return `https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`;
      case "vimeo":
        return `https://player.vimeo.com/video/${videoId}?autoplay=1&color=ffffff`;
      default:
        return "";
    }
  }

  handleItemHover(item, isEntering) {
    const video = item.querySelector("[data-gallery-video]");
    const mediaType = item.dataset.mediaType;

    if (mediaType === "video" && video) {
      if (isEntering) {
        const timeout = setTimeout(() => {
          video.play().catch(() => {
            console.log("Autoplay prevented by browser policy");
          });
        }, 300);
        this.hoverTimeouts.set(item, timeout);
      } else {
        const timeout = this.hoverTimeouts.get(item);
        if (timeout) {
          clearTimeout(timeout);
          this.hoverTimeouts.delete(item);
        }
        // Pause and reset video when hover ends
        video.pause();
        video.currentTime = 0;
      }
    }
  }

  openModal(index) {
    this.currentIndex = index;
    this.isModalOpen = true;
    this.renderModalContent();
    this.modal.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";

    setTimeout(() => {
      this.closeButton?.focus();
    }, 100);
  }

  closeModal() {
    this.isModalOpen = false;
    this.modal.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";

    const video = this.modalContent.querySelector("video");
    const iframe = this.modalContent.querySelector("iframe");

    if (video) {
      video.pause();
    }

    if (iframe) {
      iframe.src = iframe.src;
    }

    this.galleryItems[this.currentIndex]?.focus();
  }

  navigateModal(direction) {
    const newIndex = this.currentIndex + direction;

    if (newIndex >= 0 && newIndex < this.galleryItems.length) {
      this.currentIndex = newIndex;
      this.renderModalContent();
    }
  }

  renderModalContent() {
    const currentItem = this.galleryItems[this.currentIndex];
    const mediaType = currentItem.dataset.mediaType;

    this.modalContent.innerHTML = "";

    if (mediaType === "video") {
      const originalVideo = currentItem.querySelector("[data-gallery-video]");
      if (originalVideo) {
        const modalVideo = originalVideo.cloneNode(true);

        modalVideo.controls = true;
        modalVideo.autoplay = true;
        modalVideo.muted = false;
        modalVideo.loop = true;
        modalVideo.removeAttribute("data-gallery-video");

        this.modalContent.appendChild(modalVideo);
      }
    } else {
      const originalImage = currentItem.querySelector("[data-gallery-image]");
      if (originalImage) {
        const modalImage = originalImage.cloneNode(true);

        modalImage.removeAttribute("data-gallery-image");

        this.modalContent.appendChild(modalImage);
      }
    }

    this.updateNavigationState();
  }

  updateNavigationState() {
    if (this.prevButton) {
      this.prevButton.style.opacity = this.currentIndex === 0 ? "0.5" : "1";
      this.prevButton.disabled = this.currentIndex === 0;
    }

    if (this.nextButton) {
      this.nextButton.style.opacity =
        this.currentIndex === this.galleryItems.length - 1 ? "0.5" : "1";
      this.nextButton.disabled =
        this.currentIndex === this.galleryItems.length - 1;
    }
  }

  handleKeyboard(event) {
    if (!this.isModalOpen) return;

    switch (event.key) {
      case "Escape":
        this.closeModal();
        break;
      case "ArrowLeft":
        this.navigateModal(-1);
        break;
      case "ArrowRight":
        this.navigateModal(1);
        break;
    }
  }
}

document.addEventListener("DOMContentLoaded", () => {
  const gallerySections = document.querySelectorAll(
    '[data-section-type="gallery"]'
  );
  gallerySections.forEach((section) => {
    new GallerySection(section);
  });
});
